import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getTutorAssignments } from '@/lib/student-objectives/tutor-assignments';
import { SubmitExerciseModal } from '@/components/exercise-submission/submit-exercise-modal';
import type { TutorAssignmentSummary } from '@/lib/student-objectives/objective-types';
import { TutorCard } from './tutor-card';

interface Props {
  studentId: string;
  onStartExercise: (exerciseId: string) => void;
  /** Exercice en cours d'ouverture (spinner sur son bouton). */
  pendingExerciseId?: string | null;
}

/**
 * Un élève peut avoir plusieurs tuteurs particuliers actifs (un par
 * matière, ou un tuteur pour plusieurs matières — migration 0227) : une
 * `TutorCard` par lien, chacune avec son propre bouton d'envoi d'exercice
 * scopé à CE tuteur (`submitModalTutor`, pas un état partagé unique).
 */
export function MonProfSection({ studentId, onStartExercise, pendingExerciseId = null }: Props) {
  const [submitModalTutor, setSubmitModalTutor] = useState<TutorAssignmentSummary | null>(null);
  const { data: tutors, isLoading } = useQuery({
    queryKey: ['tutor-assignments', studentId],
    enabled: !!studentId,
    queryFn: () => getTutorAssignments(supabase, studentId),
    staleTime: 60 * 1000,
  });

  if (isLoading || !tutors || tutors.length === 0) return null;

  return (
    <section className="space-y-3">
      <h2 className="font-display text-ink-900 flex items-center gap-2 text-lg font-bold">
        <span aria-hidden>🎓</span>
        <span>{tutors.length > 1 ? 'Mes profs particuliers' : 'Mon prof particulier'}</span>
      </h2>
      <div className="space-y-3">
        {tutors.map((summary) => (
          <TutorCard
            key={summary.tutorId}
            summary={summary}
            onStartExercise={onStartExercise}
            pendingExerciseId={pendingExerciseId}
            onSendExercise={() => setSubmitModalTutor(summary)}
          />
        ))}
      </div>
      {submitModalTutor && (
        <SubmitExerciseModal
          studentId={studentId}
          tutorId={submitModalTutor.tutorId}
          tutorName={submitModalTutor.tutorName}
          tutorSubjects={submitModalTutor.subjects}
          onClose={() => setSubmitModalTutor(null)}
        />
      )}
    </section>
  );
}
